import { Component, ChangeDetectorRef, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import {
  UserService,
  ProductService,
  ContentService,
  EventsService,
  EventBusService,
  OrderService,
  WindowService,
  StorageService,
  StorageKeys,
  environment,
  CheckoutService,
  HelperService,
  ProductSnapshot,
} from "@mypxplat/xplat/core";
import { filter, take, takeUntil } from "rxjs/operators";
import { ActivatedRoute, Router } from "@angular/router";
import { AppService, AvoService, BetaService, DiscordGiftService, FirebaseService } from "@mypxplat/xplat/web/core";
import { SphereHomeBaseComponent } from "@mypxplat/xplat/features";
import { NgxSpinnerService } from "ngx-spinner";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import {
  RegisterProductComponent,
  RegisterSuccessSplashComponent,
  StudiooneGracePeriodComponent,
  MissedPaymentModalComponent,
  AuthenticateFenderUserComponent,
  LinkFenderAccountComponent,
} from "../modals";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { fetchAuthSession } from "aws-amplify/auth";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-dashboard-home",
  templateUrl: "dashboard-home.component.html",
  standalone: false,
})
export class DashboardHomeComponent extends SphereHomeBaseComponent implements OnInit, OnDestroy {
  public loading: boolean = true;
  public displayedSection: string = "News & Updates";
  public needsMoreData: boolean;
  public selectedVimeoID: string;
  public gettingS1Demo: boolean;
  public gettingNotionDemo: boolean;
  public hasS1App: any = false;
  public shouldSeeS1Demo: any = false;
  public hasFree: any = false;
  public hasProDemo: any = false;
  public hasNotion: any = false;
  public hasNotionDemo: any = false;
  public daysLeft: any = false;
  public showStudioProDemoCta: boolean = new Date() >= new Date(this.env.studioProDemoLaunchDate);
  public redeemingMB: boolean = false;
  public promotions: any[] = [];
  public loadingPromotions: boolean = false;
  public subscriptionPromotion: any = null;
  public otherPromotions: any[] = [];
  public newEvents: any[] = [];
  public newEventsLoading: boolean = true;
  public isSmallViewport: boolean = false;

  constructor(
    userService: UserService,
    private _router: Router,
    public appService: AppService,
    public productService: ProductService,
    public contentService: ContentService,
    public eventsService: EventsService,
    public fbAuth: AngularFireAuth,
    public db: AngularFirestore,
    private spinner: NgxSpinnerService,
    public helperService: HelperService,
    private _translate: TranslateService,
    public eventBusService: EventBusService,
    public orderService: OrderService,
    public windowService: WindowService,
    public avoService: AvoService,
    public storage: StorageService,
    public betaService: BetaService,
    public fbService: FirebaseService,
    public checkoutService: CheckoutService,
    public discordGift: DiscordGiftService,
    public activatedRoute: ActivatedRoute,
    private _cdr: ChangeDetectorRef
  ) {
    super(userService, productService, contentService, eventsService);
  }
  ngOnInit(): void {
    super.ngOnInit();
    this.appService.setPageTitle("Home");
    if (!this.user) this.spinner.show();
    fetchAuthSession().then((session) => {
      if (session?.tokens?.idToken?.payload?.["custom:paeid"]) {
        // logged in with presonus password
        if (this.userService.user.fender_cognito_user_id) {
          // is linked, so make them unify passwords.
          this.appService.showModal(LinkFenderAccountComponent, { size: "lg" });
        }
      }
    });
    if (!this.userService.gettingUserDetails) {
      this.userService.getUserDetails(false).subscribe({
        next: (result) => {
          this.spinner.hide();
          if (result.subscription?.status == "canceled" && result.subscription?.cancel_reason == "terminate bluesnap" && !result.subscription?.schedule_subscription_id) {
            if (this._router.url != "/account/plan") {
              const modal = this.appService.showModal(MissedPaymentModalComponent, { size: "lg" });
              modal.componentInstance.type = "terminate bluesnap";
            }
          } else if (result.subscription?.missed_payment) {
            if (this._router.url != "/account/plan") {
              const modal = this.appService.showModal(MissedPaymentModalComponent, { size: "lg" });
              modal.componentInstance.missedPaymentDetails = result.subscription.missed_payment;
            }
          }
        },
        error: (err) => {
          this.logout();
        },
      });
    } else {
      this.userService.userDataRefreshed$.pipe(take(1)).subscribe((result) => {
        this.spinner.hide();
      });
    }

    this.contentService.educationDataStream$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.videos = [];
        this.videosLoading = false;
        let availableVideos = result.videos.sort((a, b) => (a.created > b.created ? -1 : 1));
        if ((this.appService.videoContentType.fender && this.appService.videoContentType.presonus) || (!this.appService.videoContentType.fender && !this.appService.videoContentType.presonus)) {
          // both.
        } else {
          const selectedBrand = this.appService.videoContentType.fender ? "fender" : "presonus";
          availableVideos = availableVideos.filter((item) => item.brand == selectedBrand || item.brand == "both");
        }
        this.videos = availableVideos.filter((item, index) => index <= 3);
      }
    });
    if (this.activatedRoute.snapshot.queryParams.showLinkedAccountMessage) {
      const modal = this.appService.showModal(AuthenticateFenderUserComponent, { size: "lg" });
      modal.componentInstance.newFenderUserCreated = true;
      // then remove the query param so if you refresh your page you dont constantly see the modal.
      this._router.navigate(["."], { relativeTo: this.activatedRoute, queryParams: {} });
    }

    if (this.activatedRoute.snapshot.queryParams.register) {
      let splashId = this.activatedRoute.snapshot.queryParams.register;
      if (splashId && splashId == "mustangmicro") {
        this.registerProduct(splashId);
      } else {
        this.fbService.checkForSplash(undefined, splashId).then((result) => {
          this.registerProduct(splashId, result);
        });
      }
      // this removes the query param so if you refresh your page you dont constantly see the modal.
      this._router.navigate(["."], { relativeTo: this.activatedRoute, queryParams: {} });
    }
    if (this.activatedRoute.snapshot.queryParams.showSplash) {
      this._router.navigate([], {
        queryParams: { showSplash: null, splashProductId: null, splashProductName: null },
        queryParamsHandling: "merge",
      });
      this.fbService
        .handleFirestorePromise(() => this.fbService.doc("product_splashes/" + this.activatedRoute.snapshot.queryParams.showSplash).ref.get())
        .then((result) => {
          if (result.exists) {
            let splash: any = result.data();
            this.showSplash(splash, this.activatedRoute.snapshot.queryParams.splashProductId, this.activatedRoute.snapshot.queryParams.splashProductName);
          }
        })
        .catch(() => {});
    }

    if (!this.storage.checkTimestamp("logged_in_event_triggered")) {
      this.avoService.avoInitted$.pipe(filter((result) => !!result)).subscribe((result) => {
        const te = this.avoService.trackEvent();
        if (this.userService.appId === "fender" && te.loggedInMyFender) {
          const page = this.avoService.getPageProperties({ pageName: "Logged In" });
          const extra = {
            name: "Logged In",
            userId_: this.user["fc-id"] ?? undefined,
            identitySource: "Fender Connect",
            fcId: this.user["fc-id"] ?? undefined,
            prsId: this.user.id,
          };
          const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
          te.loggedInMyFender(payload);
        } else if (te.loggedIn) {
          te.loggedIn({
            userId_: this.user.id,
            prsId: this.user.id,
            siteId: "my_fender",
          });
        }
        this.storage.setTimestamp("logged_in_event_triggered", 24);
      });
    }
    this.retrieveUpgradableProducts();
    this.fetchPromotions();
    this.checkForDiscounts();
    // Both of the above resolve on their own schedule, and so does this. Whichever finishes
    // last does the filtering — hence re-applying here rather than only at their call sites.
    this.discordGift.load().subscribe({
      next: () => this._applyDiscordGiftToOffers(),
      error: () => {},
    });
    this.loadNewEvents();
    this.productService.redeemS17ElligibilityRetrieved$
      .pipe(
        filter((result) => !!result),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.productService.products$
          .pipe(
            filter((products) => !!products),
            takeUntil(this.destroy$)
          )
          .subscribe((result) => {
            if (result) {
              this.productsLoading = false;
              this.refreshRecentProducts();
              this.setupDemoData();
            }
          });
      });
    this.checkDataWeNeed();
    this.isSmallViewport = this.appService.isSmallViewport;
    this.appService.viewportChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isSmallViewport = this.appService.isSmallViewport;
      this.refreshRecentProducts();
      this._cdr.markForCheck();
    });
  }

  fetchPromotions() {
    this.loadingPromotions = true;
    this.userService.getPromotions().subscribe({
      next: (result: any) => {
        this.promotions = result || [];
        const sphereIds = [this.env.product_ids.sphere_monthly, this.env.product_ids.sphere_annual];
        this.subscriptionPromotion = this.promotions.find((p) => sphereIds.includes(p.offer_product?.product?.id));
        this.otherPromotions = this.promotions.filter((p) => !sphereIds.includes(p.offer_product?.product?.id));
        this._applyDiscordGiftToOffers();
        this.loadingPromotions = false;
      },
      error: () => {
        this.loadingPromotions = false;
      },
    });
  }

  public activeCoupon: any;
  public upgradableProducts: any;
  public isUpgrade: boolean = false;
  public perpetualPrice: number;
  public perpetualDiscountedPrice: number;
  checkForDiscounts() {
    this.checkoutService.getUpgradableProducts().subscribe((result) => {
      const usdPrice = result?.data?.prices?.find((price) => price.currency === "usd");
      if (!usdPrice) return;
      let perpetualDisplayPrice = usdPrice.unit_amount / 100;
      this.userService.getActiveOffers().subscribe((activeOffersResult: any) => {
        if (activeOffersResult && activeOffersResult.length) {
          activeOffersResult.forEach((coupon) => {
            let appliesToProducts = coupon.applies_to_products.split(",").map((product) => product.trim());
            if (appliesToProducts.includes(this.checkoutService.upgradableStudioOneProducts?.data?.products[0].stripeProductId)) this.activeCoupon = coupon;
          });
        }
        this.perpetualPrice = perpetualDisplayPrice;
        let amountOff = Math.floor(perpetualDisplayPrice * this.activeCoupon?.percentage) / 100;
        if (this.activeCoupon) perpetualDisplayPrice = parseFloat((perpetualDisplayPrice - amountOff).toFixed(2));
        this.perpetualDiscountedPrice = perpetualDisplayPrice;
        this._applyDiscordGiftToOffers();
        this.upgradableProducts = result.data;
        this.isUpgrade = this.upgradableProducts.products[0].title.indexOf("Upgrade") > -1;
      });
    });
  }

  /**
   * Hides the PAID Fender Studio Pro offers from anyone who can have it for nothing.
   *
   * Two of them: `activeCoupon`, the auto-applied Stripe discount on the perpetual license,
   * and any registration-triggered promotion whose offer product IS Fender Studio Pro. The
   * free block (`fui-discord-free-fsp-offer`) renders in their place, so the section is not
   * left empty by this.
   *
   * Called from both arrivals — the promotions list and the eligibility answer land
   * independently, and whichever is second has to do the filtering.
   */
  private _applyDiscordGiftToOffers() {
    if (!this.discordGift.canClaimFreeFsp) return;
    const fspId = this.env.product_ids.fsp8pro;
    this.activeCoupon = null;
    this.otherPromotions = (this.otherPromotions || []).filter((p) => p.offer_product?.product?.id != fspId);
  }

  buyPerpetualLicense() {
    this.appService.goToCheckout("/checkout?checkoutProductType=Perpetual", this.checkoutService.upgradableStudioOneProducts?.data?.products[0]?.id);
  }

  loadNewEvents() {
    if (this.eventsService.cachedPublicEvents.length) {
      this.newEvents = this.eventsService.cachedPublicEvents
        .map((e) => this.enrichEvent(e))
        .filter((e) => !this.endedBeforeToday(e))
        .slice(0, 5);
      this.newEventsLoading = false;
      return;
    }
    this.newEventsLoading = true;
    this.eventsService.getPublicEvents(50).subscribe({
      next: (result) => {
        this.newEvents = (result.events || [])
          .map((e) => this.enrichEvent(e))
          .filter((e) => !this.endedBeforeToday(e))
          .slice(0, 5);
        this.newEventsLoading = false;
      },
      error: () => {
        this.newEventsLoading = false;
      },
    });
  }

  private enrichEvent(event: any): any {
    if (event.start_datetime) {
      const date = this.parseDatetimeInTimezone(event.start_datetime, event.timezone || "America/Chicago");
      event._formattedDate = date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    }
    return event;
  }

  private endedBeforeToday(event: any): boolean {
    if (!event.start_datetime) return false;
    const tz = event.timezone || "America/Chicago";
    const endDt = event.end_datetime || event.start_datetime;
    const endTime = this.parseDatetimeInTimezone(endDt, tz);
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return endTime < startOfToday;
  }

  private parseDatetimeInTimezone(dt: string, timezone: string): Date {
    const parts = dt.split("T");
    const [y, mo, d] = parts[0].split("-").map(Number);
    const [h, mi] = (parts[1] || "00:00").split(":").map(Number);
    const fake = new Date(y!, mo! - 1, d!, h!, mi!);
    const inTz = new Date(fake.toLocaleString("en-US", { timeZone: timezone }));
    const offset = fake.getTime() - inTz.getTime();
    return new Date(fake.getTime() + offset);
  }

  goToEvent(event: any) {
    this._router.navigate(["events", event.slug || event.id]);
  }

  retrieveUpgradableProducts() {
    if (!this.checkoutService.upgradableStudioOneProducts) {
      this.checkoutService.getUpgradableProducts().subscribe();
    }
  }

  tileClick(prod) {
    if (prod?.id != null) {
      this._router.navigate(["products/detail/", prod.id]);
    }
  }

  downloadInstaller(product) {
    this.productService
      .getProductSnapshot(product.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((snapshot: ProductSnapshot) => {
        const installer = this.helperService.getInstallerForCurrentOs(snapshot?.softwareDownloads, snapshot?.product?.title, this.appService.os);
        if (installer) {
          this._getTokenizedInstaller(installer);
        } else {
          this.appService.alertError(this._translate.instant("products.no_installers_available"));
        }
      });
  }

  private _getTokenizedInstaller(dl) {
    if (dl.tokenized) {
      this.productService
        .getTokenizedUrl(dl.folder, dl.filename, "my_fender", dl.title, dl.id.toString(), dl.filename, parseInt(dl.filesize), dl.version || dl.version_number)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            window.open(result.url);
          },
          error: (error) => {
            this.appService.alertError(error);
          },
        });
    } else {
      window.open(dl.url);
    }
  }

  playSample(prod, event?: Event) {
    const button = (event?.target as HTMLElement).closest("button");
    if (button?.hasAttribute("data-focus-restored")) {
      button.removeAttribute("data-focus-restored");
      return;
    }
    this.eventBusService.emit(this.eventBusService.types.playAudio, {
      url: prod.featuredSample,
      filename: prod.title,
      triggerElement: button,
    });
  }

  private getUrlParameter(url, name) {
    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
    var regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
    var results = regex.exec(url);
    return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
  }

  showVideo(video) {
    if (this.user.active_subscription) {
      let parts = video.uri.split("/");
      this.eventBusService.emit(this.eventBusService.types.playVideo, {
        id: parts[parts.length - 1],
        title: video.name,
        type: "vimeo",
        thumb: video.pictures.sizes[3].link,
      });
    } else {
      this.upgrade();
    }
  }

  openLivestream(id) {
    let video = this.contentService.videosByPlatformId[id];
    if (video) {
      this.eventBusService.emit(this.eventBusService.types.playVideo, {
        id: video.platform_id,
        title: video.title,
        type: video.platform,
        thumb: video.image,
        presonus_id: video.id,
        presonusVideo: video,
      });
    } else {
      this.eventBusService.emit(this.eventBusService.types.playVideo, {
        id: id,
        type: "vimeo",
      });
    }
  }

  public profileProgress: any = {
    percentage: 0,
    completed: 0,
    total: 0,
  };
  checkDataWeNeed() {
    let hasAll = true;
    this.userService.dataWeNeed.forEach((item) => {
      if (!this.user[item.key]) {
        hasAll = false;
      }
    });

    this.needsMoreData = !hasAll;
    let completed = (this.profileProgress.completed = this.userService.dataWeNeed.filter((item) => {
      return item.key == "skills" ? this.user.skills?.length : this.user[item.key];
    }).length);
    this.profileProgress.percentage = Math.round((completed / this.userService.dataWeNeed.length) * 100);
    this.profileProgress.completed = completed;
    this.profileProgress.total = this.userService.dataWeNeed.length;
  }

  openUrl(url) {
    window.open(url);
  }

  getFreeProduct(product) {
    if (product.indexOf("notion") > -1) {
      this.gettingNotionDemo = true;
    } else {
      this.gettingS1Demo = true;
    }
    this.productService.getFreeProduct(product, "mypresonus").subscribe((result: any) => {
      const te = this.avoService.trackEvent();
      const productId = this.gettingNotionDemo ? "395" : environment.product_ids.fsp8demo;
      const productName = this.gettingNotionDemo ? "Notion 6 Demo" : "Studio One 6 Demo";
      if (this.userService.appId === "fender" && te.transactionCompletedMyfender) {
        const page = this.avoService.getPageProperties({ pageName: "Free Product" });
        const extra = {
          name: "Free Product",
          userId_: this.user["fc-id"] ?? undefined,
          fcId: this.user["fc-id"] ?? undefined,
          prsId: this.user.id,
          products: [
            {
              currency: "USD",
              price: 0,
              quantity: 1,
              sku: productId,
              variant: "",
              category: "",
              position: 0,
              productName,
              productId,
              upsell: false,
            },
          ],
          total: 0,
          subtotal: 0,
          currency: "USD",
          tax: 0,
          isGuestCheckout: false,
          paymentType: "free",
          paymentMethod: "free",
          orderId: "",
          acceptsMarketing: "false",
        };
        const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
        te.transactionCompletedMyfender(payload);
      } else if (te.orderCompleted) {
        const page = this.avoService.getPageProperties({ pageName: "Free Product" });
        const extra = {
          prsId: this.user.id,
          subProvider: this.user.subscription?.provider || undefined,
          subStartDate: this.user.subscription?.start_date || undefined,
          subEndDate: this.user.subscription?.end_date || undefined,
          subType: this.user.subscription?.type || undefined,
          prsSubStatus: this.user.subscription?.status || undefined,
          subtotal: 0,
          price: 0.0,
          quantity: 1,
          products: [{ productId, price: 0.0, sku: productId, name: productName }],
          userId_: this.user.id,
        };
        const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
        te.orderCompleted(payload);
      }
      this.productService.getProducts(true).subscribe(() => {
        if (result.licenseId) {
          if (this.gettingS1Demo) {
            this.fbService.checkForSplash("studioapp8.demo").then((splash) => {
              this.showSplash(splash, environment.product_ids.fsp8demo, "Studio One 7 Demo", true);
              this._router.navigate(["products/detail", result.licenseId]);
            });
          } else {
            this._router.navigate(["products/detail", result.licenseId]);
          }
        }
        this.gettingS1Demo = false;
        this.gettingNotionDemo = false;
      });
    });
  }

  showS16Redeem() {
    const modalRef = this.appService.showModal(StudiooneGracePeriodComponent, {
      size: "lg",
      container: this.appService.appContainer,
    });
  }

  showSplash(splash, productId?, productName?, isNewRegistration?) {
    const modalRef = this.appService.showModal(RegisterSuccessSplashComponent, {
      size: "lg",
      container: this.appService.appContainer,
    });
    modalRef.componentInstance.splash = splash;
    if (productId) modalRef.componentInstance.productId = productId;
    if (productName) modalRef.componentInstance.productName = productName;
    if (isNewRegistration) modalRef.componentInstance.isNewRegistration = true;
  }

  setupDemoData() {
    this.hasS1App = this.productService.productsByErpIDs["STUDIOONE7"];
    this.shouldSeeS1Demo =
      !this.user.active_subscription && !this.productService.productsByStringIDs["studioapp8.demo"] && !this.productService.productsByStringIDs["studioapp8.pro"] && this.env.features.s17_demo;
    this.hasFree = this.productService.productsByStringIDs["studioapp7.free"];
    this.hasProDemo = this.productService.productsByStringIDs["studioapp8.demo"];
    this.hasNotion = this.productService.productsByStringIDs["notion6"];
    this.hasNotionDemo = this.productService.productsByStringIDs["notion6.demo"];
    if (this.hasProDemo) {
      if (this.hasProDemo.expirationDate) {
        const expires = moment(this.hasProDemo.expirationDate);
        const today = moment();
        this.daysLeft = today.diff(expires, "days") * -1;
        if (this.daysLeft < 0) {
          this.daysLeft = 0;
        } else {
          this.daysLeft = 30;
        }
      }
    }
  }

  redeemPromotion(promo: any) {
    this.storage.setItem(StorageKeys.CHECKOUT_COUPONS, [promo.coupon_code]);
    // Persist the promotion's discount magnitude so checkout can compare it against the
    // auto-apply active offer and favor the better deal.
    this.storage.setItem(StorageKeys.PROMOTION_COUPON, {
      code: promo.coupon_code,
      percent_off: promo.coupon?.percent_off || 0,
      amount_off: promo.coupon?.amount_off || 0,
      currency: promo.coupon?.currency || null,
    });
    const sphereIds = [this.env.product_ids.sphere_monthly, this.env.product_ids.sphere_annual];
    if (sphereIds.includes(promo.offer_product?.product?.id)) {
      const term = promo.offer_product.product.id === this.env.product_ids.sphere_monthly ? "monthly" : "annual";
      this.appService.goToCheckout("/checkout?term=" + term);
    } else {
      this.appService.goToCheckout("/checkout?checkoutProductType=Perpetual");
    }
  }

  upgrade() {
    this._router.navigate(["/onboarding"], { queryParams: { subscribeOnly: true } });
  }

  logout() {
    this._router.navigate(["/logout"]);
  }

  registerProduct(splashId?, splashData?) {
    let regInProcessString;
    this._translate.get("products.register.registration_in_process").subscribe((string) => {
      regInProcessString = string;
    });
    const modalRef = this.appService.showModal(RegisterProductComponent, {
      size: splashId == "mustangmicro" || splashData?.registerCustomUI ? "md" : "lg",
      ariaLabelledBy: "modal-title",
      container: this.appService.appContainer,
      beforeDismiss: () => {
        if (modalRef.componentInstance.registering) {
          alert(regInProcessString);
          return false;
        }
      },
    });
    // there is special handling for mustang micro registrations.
    if (splashId == "mustangmicro") {
      modalRef.componentInstance.mustang = true;
    } else if (splashData?.registerCustomUI) {
      modalRef.componentInstance.registerCustomUI = splashData.registerCustomUI;
    }
  }

  redeemS17() {
    const modalRef = this.appService.showModal(StudiooneGracePeriodComponent, {
      size: "lg",
      container: this.appService.appContainer,
    });
    modalRef.componentInstance.asset = this.productService.s16ProductToUpgradeTo7;
  }

  private getHomeProductRailCapacity(): number {
    const width = window.innerWidth;
    if (width <= AppService.SMALL_VIEWPORT_MAX_WIDTH) return 6;
    if (width <= AppService.MEDIUM_VIEWPORT_MAX_WIDTH) return 6;
    if (width <= AppService.LARGE_VIEWPORT_MAX_WIDTH) return 8;
    return 5;
  }

  private refreshRecentProducts(): void {
    if (!this.productService.productsByAcquired?.length) return;
    const reservedSlots = 1 + (this.productService.elligleToRedeemStudioOneSeven ? 1 : 0);
    const recentProductLimit = this.getHomeProductRailCapacity() - reservedSlots;
    this.recentProducts = this.productService.productsByAcquired.filter((item, index) => index < recentProductLimit);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.appService.fullscreenHeader = undefined;
  }
}
